import { useId } from "react";

// Lets you branch a connection off another node in your network instead of
// off yourself. Reused by the add and edit modals.
//
// value shape: { parentUid: string|null, parentName: string, mode: "sub"|"direct"|null }
// A null parentUid means "branches off you" (default).

const DEGREE_GROUPS = [
  { degree: 1, label: "Your connections" },
  { degree: 2, label: "2nd degree — friends of connections" },
  { degree: 3, label: "3rd degree" },
];

function nameOf(node) {
  return node.profile?.full_name || "Unknown";
}

function sublabelOf(node) {
  return node.profile?.role || node.profile?.school || node.profile?.industry || "";
}

export default function BranchPicker({
  candidates,
  value,
  onChange,
  currentUid,
  excludeIds,
  id,
  label = "How are you connected?",
  disabled = false,
}) {
  const generatedId = useId();
  const selectId = id || `branch-picker-${generatedId.replace(/:/g, "")}`;
  const exclude = excludeIds instanceof Set ? excludeIds : new Set(excludeIds || []);

  const options = (candidates || [])
    .filter((n) => n.id !== currentUid && n.degree >= 1 && !exclude.has(n.id))
    // de-dupe by id (a node can appear once)
    .reduce((acc, n) => {
      if (!acc.some((o) => o.id === n.id)) acc.push(n);
      return acc;
    }, [])
    .sort((a, b) => (a.degree - b.degree) || nameOf(a).localeCompare(nameOf(b)));

  const parentUid = value?.parentUid || "";
  const mode = value?.mode || "sub";

  const handleSelect = (e) => {
    const id = e.target.value;
    if (!id) {
      onChange({ parentUid: null, parentName: "", mode: null });
      return;
    }
    const node = options.find((o) => o.id === id);
    onChange({ parentUid: id, parentName: nameOf(node), mode: value?.mode || "sub" });
  };

  const parentName = value?.parentName || "this person";

  return (
    <div className="branch-picker">
      <label htmlFor={selectId}>{label}</label>
      <select id={selectId} value={parentUid} onChange={handleSelect} disabled={disabled}>
        <option value="">Directly — branches off you</option>
        {DEGREE_GROUPS.map((g) => {
          const groupOpts = options.filter((o) => o.degree === g.degree);
          if (groupOpts.length === 0) return null;
          return (
            <optgroup key={g.degree} label={g.label}>
              {groupOpts.map((o) => {
                const sub = sublabelOf(o);
                return (
                  <option key={o.id} value={o.id}>
                    {nameOf(o)}{sub ? ` · ${sub}` : ""}
                  </option>
                );
              })}
            </optgroup>
          );
        })}
      </select>

      {options.length === 0 && (
        <p className="branch-picker-empty">
          Add a few connections first to branch new people off them.
        </p>
      )}

      {parentUid && (
        <div className="branch-mode-toggle">
          <button
            type="button"
            className={`branch-mode-option ${mode === "sub" ? "active" : ""}`}
            onClick={() => onChange({ ...value, mode: "sub" })}
            aria-pressed={mode === "sub"}
            disabled={disabled}
          >
            <span className="branch-mode-title">They're {parentName}'s connection</span>
            <span className="branch-mode-desc">Hangs off {parentName}, not a direct contact of yours</span>
          </button>
          <button
            type="button"
            className={`branch-mode-option ${mode === "direct" ? "active" : ""}`}
            onClick={() => onChange({ ...value, mode: "direct" })}
            aria-pressed={mode === "direct"}
            disabled={disabled}
          >
            <span className="branch-mode-title">I met them through {parentName}</span>
            <span className="branch-mode-desc">Still your direct contact, tagged "via {parentName}"</span>
          </button>
        </div>
      )}
    </div>
  );
}
